import React, { useState } from 'react';
import { Sparkles, X, MoveHorizontal } from 'lucide-react';
import { realisations } from '../data/mock';
import BeforeAfterSlider from './BeforeAfterSlider';

const Realisations = () => {
  const [activeImage, setActiveImage] = useState(null);

  return (
    <section
      id="realisations"
      className="section-padding bg-white relative overflow-hidden"
      data-testid="realisations-section"
    >
      {/* Decorative blobs */}
      <div className="absolute top-32 right-0 w-72 h-72 bg-[hsl(207,90%,54%)] rounded-full opacity-5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[hsl(32,95%,55%)] rounded-full opacity-5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-[hsl(32,95%,55%)] font-semibold text-sm uppercase tracking-wider mb-4">
            <Sparkles className="w-4 h-4" />
            Réalisations
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Nos services{' '}
            <span className="text-gradient">effectués</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Découvrez la différence en images. Chaque chantier est réalisé avec soin pour un résultat impeccable.
          </p>
        </div>

        {/* Gallery grid */}
        <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
          {realisations.map((item, index) => (
            <div
              key={item.id}
              data-testid={`realisation-card-${item.id}`}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 animate-fade-in-up"
              style={{ animationDelay: `${index * 90}ms` }}
            >
              {/* Interactive slider */}
              <div className="relative">
                <BeforeAfterSlider
                  composite={item.image}
                  alt={item.title}
                  testId={`slider-${item.id}`}
                />
                {/* Tag */}
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-gray-900 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg pointer-events-none">
                  <MoveHorizontal className="w-3 h-3" />
                  Glissez pour comparer
                </span>
              </div>

              {/* Caption */}
              <div className="p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="font-bold text-gray-900 text-lg sm:text-xl group-hover:text-[hsl(207,90%,54%)] transition-colors">
                    {item.title}
                  </h3>
                  <span className="shrink-0 inline-block bg-blue-50 text-[hsl(207,90%,44%)] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                    {item.tag}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
                <button
                  type="button"
                  onClick={() => setActiveImage(item)}
                  data-testid={`realisation-fullscreen-${item.id}`}
                  className="mt-4 text-sm font-semibold text-[hsl(207,90%,54%)] hover:text-[hsl(207,90%,44%)] transition-colors inline-flex items-center gap-1.5"
                >
                  Voir en plein écran →
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <p className="text-gray-600 mb-6 text-lg">
            Envie d'un résultat similaire chez vous ?
          </p>
          <a
            href="#reservation"
            data-testid="realisations-cta-button"
            className="inline-flex items-center gap-2 bg-[hsl(32,95%,55%)] hover:bg-[hsl(32,95%,45%)] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
          >
            Demander mon devis gratuit
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {activeImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setActiveImage(null)}
          data-testid="realisations-lightbox"
        >
          <button
            type="button"
            onClick={() => setActiveImage(null)}
            data-testid="realisations-lightbox-close"
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-colors"
            aria-label="Fermer"
          >
            <X className="w-6 h-6" />
          </button>
          <div
            className="max-w-3xl w-full max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full rounded-xl overflow-hidden shadow-2xl">
              <BeforeAfterSlider
                composite={activeImage.image}
                alt={activeImage.title}
                testId="lightbox-slider"
              />
            </div>
            <div className="mt-4 text-center text-white">
              <h3 className="text-xl font-bold">{activeImage.title}</h3>
              <p className="text-white/70 text-sm mt-1 max-w-xl mx-auto">{activeImage.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Realisations;
